import React, { useEffect, useRef, useState } from 'react';
import { apiResponseError, parseJsonResponse } from '../constants';
import { formatChatMessageForDisplay } from '../utils/markdownDisplay';
import {
  TIER1_REPORT_CHAT_KIND,
  tier1ReportChatMessagesFromConversation,
} from '../utils/tier1ReportChatMessages';

function normalizeTier1Messages(messages) {
  return tier1ReportChatMessagesFromConversation(messages || []);
}

export default function LegendReportChat({
  apiFetch,
  sessionId,
  reportLanguage,
  disabled = false,
  savedMessages = [],
  onMessagesChange,
  onConversationRefresh,
  onHtmlUpdated,
  onError,
}) {
  const [messages, setMessages] = useState([]);
  const [questionInput, setQuestionInput] = useState('');
  const [changeInput, setChangeInput] = useState('');
  const [asking, setAsking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyFeedback, setApplyFeedback] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const applyFeedbackRef = useRef(null);
  const scrollRef = useRef(null);
  const cancelledRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const resumeCheckedRef = useRef(false);
  const savedMessagesRef = useRef(savedMessages);
  savedMessagesRef.current = savedMessages;

  useEffect(() => {
    sessionIdRef.current = sessionId;
    resumeCheckedRef.current = false;
    setMessages(normalizeTier1Messages(savedMessagesRef.current));
    setQuestionInput('');
    setChangeInput('');
    setApplyFeedback(null);
    setProgressMessage('');
    setProgressPercent(0);
    setAsking(false);
    setApplying(false);
    cancelledRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, asking, applying, applyFeedback, progressMessage, progressPercent]);

  useEffect(() => {
    if (applyFeedback) {
      applyFeedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [applyFeedback]);

  const historyPayload = () =>
    messages.map(({ role, content, timestamp }) => ({
      role,
      content,
      timestamp,
    }));

  const applyStatusProgress = (status) => {
    if (status.message && status.status === 'running') {
      setProgressMessage(status.message);
    }
    if (typeof status.progress === 'number') {
      setProgressPercent(Math.min(100, Math.max(0, status.progress)));
    }
  };

  const pollReportChatStatus = async () => {
    const params = new URLSearchParams({ session_id: sessionId, _t: String(Date.now()) });
    const response = await apiFetch(`/api/reports/legend/chat/status?${params}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorData = await parseJsonResponse(response, {});
      throw new Error(apiResponseError(response, errorData, 'Report chat status check failed.'));
    }
    return parseJsonResponse(response, {});
  };

  const waitForReportChatResult = async () => {
    const deadline = Date.now() + 30 * 60 * 1000;
    while (Date.now() < deadline) {
      if (cancelledRef.current) {
        throw new Error('Report chat was cancelled.');
      }
      const status = await pollReportChatStatus();
      applyStatusProgress(status);
      if (status.status === 'completed') {
        setProgressPercent(100);
        return status;
      }
      if (status.status === 'failed') {
        throw new Error(status.error || status.message || 'Report chat failed.');
      }
      if (status.status === 'cancelled') {
        throw new Error('Report chat was cancelled.');
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    throw new Error('Report chat timed out. Try a shorter question or try again.');
  };

  useEffect(() => {
    if (!sessionId || disabled || resumeCheckedRef.current) {
      return undefined;
    }
    resumeCheckedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        const status = await pollReportChatStatus();
        if (cancelled || sessionIdRef.current !== sessionId) {
          return;
        }
        if (status.status !== 'running') {
          return;
        }
        const isApply = String(status.message || '').toLowerCase().includes('apply');
        if (isApply) {
          setApplying(true);
        } else {
          setAsking(true);
        }
        applyStatusProgress(status);
        const result = await waitForReportChatResult();
        if (cancelled || sessionIdRef.current !== sessionId) {
          return;
        }
        if (result.status === 'completed') {
          await onConversationRefresh?.();
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Could not resume in-flight report chat:', err);
        }
      } finally {
        if (!cancelled && sessionIdRef.current === sessionId) {
          setAsking(false);
          setApplying(false);
          setProgressMessage('');
          setProgressPercent(0);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, disabled]);

  const publishMessages = (nextMessages) => {
    setMessages(nextMessages);
    onMessagesChange?.(nextMessages);
  };

  const startReportChat = async (body) => {
    const response = await apiFetch('/api/reports/legend/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!response.ok && response.status !== 202) {
      const errorData = await parseJsonResponse(response, {});
      throw new Error(apiResponseError(response, errorData, 'Report chat failed.'));
    }
    if (response.status === 200) {
      const data = await parseJsonResponse(response, {});
      const reply = (data.reply || data.message || '').trim();
      if (reply && reply !== 'Completed.') {
        return { ...data, status: 'completed', reply };
      }
    }
    setProgressPercent(5);
    return waitForReportChatResult();
  };

  const handleAsk = async () => {
    const text = questionInput.trim();
    if (!text || asking || applying || disabled) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      messageKind: TIER1_REPORT_CHAT_KIND,
    };
    const nextHistory = [...messages, userMessage];
    publishMessages(nextHistory);
    setQuestionInput('');
    setAsking(true);
    setApplyFeedback(null);
    setProgressMessage('Thinking about your question…');
    setProgressPercent(5);
    onError?.(null);
    cancelledRef.current = false;

    try {
      const priorHistory = nextHistory.slice(0, -1).map(({ role, content, timestamp }) => ({
        role,
        content,
        timestamp,
      }));
      const data = await startReportChat({
        session_id: sessionId,
        message: text,
        conversation_history: priorHistory,
        report_language: reportLanguage,
        chat_mode: 'question',
      });
      const answer = (data.reply || data.message || '').trim();
      if (!answer || answer === 'Completed.') {
        throw new Error('The assistant returned an empty answer. Try asking again.');
      }
      publishMessages([
        ...nextHistory,
        {
          role: 'assistant',
          content: answer,
          timestamp: new Date().toISOString(),
          messageKind: TIER1_REPORT_CHAT_KIND,
        },
      ]);
    } catch (err) {
      publishMessages(nextHistory.filter((m) => m.timestamp !== userMessage.timestamp));
      setQuestionInput(text);
      onError?.(err.message || 'Report chat failed');
    } finally {
      setAsking(false);
      setProgressMessage('');
      setProgressPercent(0);
    }
  };

  const handleApplyChanges = async () => {
    const text = changeInput.trim();
    if (!text || asking || applying || disabled) return;

    setApplying(true);
    setApplyFeedback(null);
    setProgressMessage('Applying playbook changes…');
    setProgressPercent(5);
    onError?.(null);
    cancelledRef.current = false;

    try {
      const data = await startReportChat({
        session_id: sessionId,
        message: text,
        conversation_history: historyPayload(),
        report_language: reportLanguage,
        chat_mode: 'apply_changes',
      });
      const reply = (data.reply || data.message || '').trim();
      if (data.html_updated && data.html_content) {
        onHtmlUpdated?.({
          html: data.html_content,
          title: data.report_title,
        });
        const summary = reply && reply !== 'Completed.' ? reply : 'Playbook updated.';
        setApplyFeedback({
          type: 'success',
          message: summary,
        });
        setChangeInput('');
        publishMessages([
          ...messages,
          {
            role: 'user',
            content: `[Report change] ${text}`,
            timestamp: new Date().toISOString(),
            messageKind: TIER1_REPORT_CHAT_KIND,
          },
          {
            role: 'assistant',
            content: summary,
            htmlUpdated: true,
            timestamp: new Date().toISOString(),
            messageKind: TIER1_REPORT_CHAT_KIND,
          },
        ]);
      } else {
        setApplyFeedback({
          type: 'error',
          message: reply || 'The model did not return updated HTML. Try rephrasing your change request.',
        });
      }
    } catch (err) {
      onError?.(err.message || 'Could not apply report changes');
      setApplyFeedback({
        type: 'error',
        message: err.message || 'Could not apply report changes',
      });
    } finally {
      setApplying(false);
      setProgressMessage('');
      setProgressPercent(0);
    }
  };

  const busy = asking || applying;

  return (
    <div className="rounded-md border border-[#3a342c] bg-[#101418] overflow-hidden space-y-0">
      <div className="px-4 py-3 border-b border-[#3a342c]">
        <h3 className="text-sm font-semibold text-[#c4a35a]">Post-report production chat</h3>
        <p className="text-xs text-[#9a9184] mt-1">
          Ask about the playbook after generation. Answers do not change the HTML until you apply changes.
        </p>
      </div>

      {busy && (
        <div className="px-4 pt-3">
          <div className="rounded-lg border border-[#3a342c] bg-gray-900/80 p-3">
            <div className="h-2 rounded-full bg-gray-800 overflow-hidden mb-2">
              <div
                className="h-full bg-[#c4a35a] transition-all duration-300"
                style={{ width: `${Math.min(100, progressPercent)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400">
              {progressMessage || (applying ? 'Applying changes to the report…' : 'Thinking…')}
            </p>
          </div>
        </div>
      )}

      <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && !asking && (
          <p className="text-sm text-gray-500">
            Example: &quot;Rewrite Case Posture for trial, not appeal.&quot; or &quot;What did my direction require?&quot;
          </p>
        )}
        {messages.map((msg, idx) => (
          <div
            key={`${msg.timestamp}-${idx}`}
            className={`text-sm rounded-lg px-3 py-2 ${
              msg.role === 'user'
                ? 'bg-[#c4a35a]/10 border border-[#3a342c] text-gray-100 ml-8'
                : 'bg-gray-800 border border-gray-700 text-gray-200 mr-8'
            }`}
          >
            <p className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
              {msg.role === 'user' ? 'You' : 'Legend'}
              {msg.htmlUpdated && (
                <span className="ml-2 text-[#c4a35a] normal-case tracking-normal">Playbook updated</span>
              )}
            </p>
            <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap">
              {formatChatMessageForDisplay(msg)}
            </div>
          </div>
        ))}
        <div ref={scrollRef} />
      </div>

      <div className="p-4 border-t border-[#3a342c] flex gap-2">
        <textarea
          value={questionInput}
          onChange={(e) => setQuestionInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleAsk();
            }
          }}
          disabled={disabled || busy}
          rows={2}
          placeholder="Ask a question about the playbook…"
          className="flex-1 px-3 py-2 rounded-lg bg-gray-950 border border-[#3a342c] text-white text-sm resize-none focus:ring-2 focus:ring-[#c4a35a]"
        />
        <button
          type="button"
          onClick={handleAsk}
          disabled={disabled || busy || !questionInput.trim()}
          className="self-end px-4 py-2 rounded-lg bg-[#c4a35a] hover:bg-[#c4a35a] disabled:opacity-50 text-white text-sm font-medium shrink-0"
        >
          Ask
        </button>
      </div>

      <div className="px-4 py-3 border-t border-[#3a342c] bg-gray-950/40">
        <h3 className="text-sm font-semibold text-[#c4a35a]">Apply playbook changes</h3>
        <p className="text-xs text-gray-500 mt-1">
          Describe additions or edits (e.g. “Remove appeal framing; keep trial posture”). Prior Q&amp;A is included. Changes update the preview when applied.
        </p>
        <textarea
          value={changeInput}
          onChange={(e) => setChangeInput(e.target.value)}
          disabled={disabled || busy}
          rows={3}
          placeholder="Example: Replace appeal strategy with trial posture and emphasize summary judgment timing."
          className="mt-3 w-full px-3 py-2 rounded-lg bg-gray-950 border border-[#3a342c] text-white text-sm resize-none focus:ring-2 focus:ring-[#c4a35a]"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleApplyChanges}
            disabled={disabled || busy || !changeInput.trim()}
            className="px-4 py-2 rounded-lg bg-[#8a6a2f] hover:bg-[#c4a35a] disabled:opacity-50 text-white text-sm font-medium"
          >
            {applying ? 'Applying changes…' : 'Apply changes to playbook'}
          </button>
          {applyFeedback && (
            <p
              ref={applyFeedbackRef}
              className={`text-sm ${
                applyFeedback.type === 'success' ? 'text-[#c4a35a]' : 'text-[#e0c27a]'
              }`}
            >
              {applyFeedback.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
